import React, { useEffect, useRef, useState } from 'react';
import { ChevronUp, Layers } from 'lucide-react';
import { RobbieMark, Tooltip } from '../../ui';
import { OutreachAction, SupplierThread } from './types';
import { isCounterCard } from '../../../hooks/outreachActionModel';
import { useRowOverflow } from '../../../hooks/useRowOverflow';
import CounterOfferCard from './CounterOfferCard';
import MovePill from './MovePill';
import MovesSheet from './MovesSheet';
import { useReadOnly } from '../../../contexts/WizardAccessContext';

/**
 * Robbie's open moves on this thread, compressed to ONE row above the
 * composer. The card band this replaces stacked a card per move and starved
 * the conversation of the height it exists for; the moves are the same, the
 * rules are the same, and the detail is a hover or a tap away instead of
 * permanently on screen.
 *
 * Nothing on this bar reaches a supplier. Tapping a pill writes that ask into
 * the buyer's box and stops. The counter is the one pill that opens instead of
 * staging — its number has to be confirmed first, and an unconfirmed counter
 * never joins the bundle either. Both the counter's confirm step and the detail
 * sheet OVERLAY the conversation from the bar, so opening one moves nothing.
 *
 * A viewer keeps the bar as a read — how many moves are open, and the full
 * reasoning behind each in the sheet — and loses every control that would put
 * one in a box they do not have.
 */
export type MovesOverlay = 'counter' | 'sheet';

interface MovesBarProps {
  thread: SupplierThread;
  actions: OutreachAction[];
  /** Writes these asks into the composer as the buyer's draft. Sends nothing. */
  onStage: (actions: OutreachAction[], approvedPrice?: number) => void;
  onDismiss: (actionId: string) => void;
  /** Ids whose asks are already in the box, waiting on the buyer's send. */
  stagedIds: string[];
  /** A send is in flight — the bar holds still until it lands. */
  busy?: boolean;
  /** Which overlay starts open. The /design gallery shows both states with it. */
  initialOverlay?: MovesOverlay;
}

const MovesBar: React.FC<MovesBarProps> = ({
  thread,
  actions,
  onStage,
  onDismiss,
  stagedIds,
  busy,
  initialOverlay,
}) => {
  const readOnly = useReadOnly();
  const [overlay, setOverlay] = useState<MovesOverlay | null>(initialOverlay ?? null);
  // The counter's number is confirmed on its own step; until it is, neither
  // that pill nor the bundle may write an unapproved price into the box.
  const [confirmedPrice, setConfirmedPrice] = useState<number | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const { rowRef, visibleCount } = useRowOverflow(actions.length);

  useEffect(() => {
    if (!overlay) return undefined;
    const close = () => setOverlay(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: MouseEvent) => {
      if (!barRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [overlay]);

  if (actions.length === 0) {
    return (
      <div className="flex shrink-0 items-center gap-s3 border-t border-line-divider-soft bg-surface-hover px-[16px] py-s2">
        <RobbieMark size="xxs" state={busy ? 'thinking' : undefined} aria-label="Robbie is watching this thread" />
        <span className="text-meta leading-[1.4] text-ink-secondary">
          {busy
            ? `Robbie is writing to ${thread.supplierName}…`
            : 'Nothing here needs you yet. I’ll put a move here the moment this thread moves.'}
        </span>
      </div>
    );
  }

  const isStaged = (action: OutreachAction) => stagedIds.includes(action.id);
  const stagedCount = actions.filter(isStaged).length;
  const outOfBox = actions.filter((action) => !isStaged(action));
  const bundled = outOfBox.filter((action) => !isCounterCard(action) || confirmedPrice != null);
  const counter = actions.find(isCounterCard);
  const counterHeldBack = outOfBox.some(isCounterCard) && confirmedPrice == null;
  const clipped = Math.max(0, actions.length - visibleCount);

  const status = readOnly
    ? `${actions.length} move${actions.length === 1 ? '' : 's'} Robbie would make with ${thread.supplierName}. Making them is the RFQ owner's call.`
    : busy
    ? `Robbie is writing to ${thread.supplierName}…`
    : stagedCount > 0
      ? `${stagedCount} ${stagedCount === 1 ? 'ask is' : 'asks are'} in your box and nothing has gone out — change any of them, then send when you’re happy.`
      : `${actions.length} move${actions.length === 1 ? '' : 's'} I’d make with ${thread.supplierName}. Tap one and it lands in your box — nothing goes out until you send.`;

  const toggle = (next: MovesOverlay) => setOverlay((prev) => (prev === next ? null : next));

  return (
    <div ref={barRef} className="relative shrink-0 border-t border-line-divider-soft bg-ai-panel px-[12px] py-s2">
      {overlay && (
        <div className="absolute inset-x-0 bottom-full z-20 px-[12px] pb-s2">
          {overlay === 'sheet' ? (
            <MovesSheet
              thread={thread}
              actions={actions}
              onStage={onStage}
              onDismiss={onDismiss}
              stagedIds={stagedIds}
              onConfirmPrice={setConfirmedPrice}
              busy={busy}
            />
          ) : (
            counter && (
              <div className="rounded-card shadow-floating">
                <CounterOfferCard
                  thread={thread}
                  action={counter}
                  onStage={(approvedPrice) => {
                    onStage([counter], approvedPrice);
                    setOverlay(null);
                  }}
                  onDismiss={() => {
                    onDismiss(counter.id);
                    setOverlay(null);
                  }}
                  onConfirmPrice={setConfirmedPrice}
                  staged={isStaged(counter)}
                  disabled={busy}
                />
              </div>
            )
          )}
        </div>
      )}

      <div className="flex items-center gap-s2">
        <RobbieMark size="xxs" state={busy ? 'thinking' : undefined} aria-label="Robbie's suggested moves" />
        <Tooltip content={status} className="shrink-0">
          <span className="whitespace-nowrap text-meta font-bold text-ink">
            {actions.length} move{actions.length === 1 ? '' : 's'}
            {stagedCount > 0 && <span className="font-normal text-ink-muted"> · {stagedCount} in your box</span>}
          </span>
        </Tooltip>

        <div ref={rowRef} className="flex h-[22px] min-w-0 flex-1 flex-wrap items-center gap-s2 overflow-hidden">
          {actions.map((action, idx) => (
            <MovePill
              key={action.id}
              action={action}
              staged={isStaged(action)}
              clipped={idx >= visibleCount}
              active={overlay === 'counter' && isCounterCard(action)}
              needsConfirm={isCounterCard(action) && !isStaged(action) && confirmedPrice == null}
              confirmed={isCounterCard(action) && !isStaged(action) && confirmedPrice != null}
              disabled={busy}
              onClick={() => (isCounterCard(action) ? toggle('counter') : onStage([action]))}
              // The pill itself renders inert in read-only; this stays wired
              // for the owner's path only.
            />
          ))}
        </div>

        {clipped > 0 && (
          <Tooltip
            content={
              <>
                <span className="block font-semibold">
                  {clipped} more move{clipped === 1 ? '' : 's'} — open the detail to work on{' '}
                  {clipped === 1 ? 'it' : 'them'}:
                </span>
                {actions.slice(visibleCount).map((action) => (
                  <span key={action.id} className="mt-s1 block">
                    · {action.title}
                  </span>
                ))}
              </>
            }
          >
            <button
              type="button"
              onClick={() => toggle('sheet')}
              className="shrink-0 rounded-pill border border-robbie-border bg-surface px-[9px] py-[3px] text-meta font-semibold text-robbie-ink hover:border-robbie"
            >
              +{clipped}
            </button>
          </Tooltip>
        )}

        {bundled.length > 1 && !readOnly && (
          <Tooltip
            content={
              counterHeldBack
                ? 'I write these asks into your box as one query. The counter stays out until you set its number.'
                : "I write these asks into your box as one query — edit them, then send when you're happy."
            }
          >
            <button
              type="button"
              onClick={() => onStage(bundled, confirmedPrice ?? undefined)}
              disabled={busy}
              className="inline-flex shrink-0 items-center gap-s2 whitespace-nowrap rounded-control border border-action-border bg-surface px-[9px] py-[3px] text-meta font-semibold text-action-deep hover:bg-action-bg disabled:pointer-events-none disabled:opacity-50"
            >
              <Layers className="h-3 w-3" />
              {stagedCount > 0 ? `Put the other ${bundled.length} in` : `Put all ${bundled.length} in the box`}
            </button>
          </Tooltip>
        )}

        <Tooltip content="The full reasoning behind every move, over the conversation.">
          <button
            type="button"
            onClick={() => toggle('sheet')}
            aria-expanded={overlay === 'sheet'}
            aria-label="Show the detail behind these moves"
            className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-control text-robbie-ink hover:bg-robbie-bg"
          >
            <ChevronUp className={`h-4 w-4 transition-transform ${overlay === 'sheet' ? 'rotate-180' : ''}`} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

export default MovesBar;
